/**
 * VB Server - Alerts API
 *
 * CRUD operations for organization alerts (keyword notifications).
 *
 * @example
 * import { listAlerts, createAlert, updateAlert, deleteAlert } from '@/api/server/alerts';
 *
 * const alerts = await listAlerts(orgId);
 * const newAlert = await createAlert(orgId, { keyword: 'emergency' });
 */

import { serverGet, serverPost, serverPut, serverDelete } from './client';

// =============================================================================
// Types
// =============================================================================

/**
 * Alert address configuration
 */
export interface AlertAddress {
  type: 'email' | 'sms' | 'tel' | 'webhook' | 'slack' | 'whatsapp';
  address: string;
  enabled: boolean;
  name?: string;
  /**
   * True when a webhook has a server-generated signing secret. The raw secret is
   * never returned in bulk responses; fetch it via {@link revealWebhookSecret}.
   */
  secret_set?: boolean;
  /** Masked preview of the signing secret, e.g. `whsec_…xyz9`. Never the full value. */
  secret_hint?: string;
  /** Per-webhook audio-link expiry override, hours (1..720; default 24). */
  link_expiry_hours?: number;
}

/**
 * Address shape accepted on create/update. The webhook signing secret is
 * read-only and server-authoritative, so the reveal-only `secret_set` /
 * `secret_hint` fields are omitted from write payloads — the client never sends them.
 */
export type AlertAddressInput = Omit<AlertAddress, 'secret_set' | 'secret_hint'>;

/**
 * Per-alert notification policy. All fields optional; the server fills defaults.
 * `unlimited` bypasses the rate budget (webhook-only). `inApp` toggles toasts.
 */
export interface NotifyPolicy {
  maxPerWindow?: number;
  windowMinutes?: number;
  digestMinutes?: number;
  unlimited?: boolean;
  inApp?: boolean;
}

/**
 * Whether a field filter includes or excludes the listed values.
 */
export type FilterMode = 'include' | 'exclude';

/**
 * Fields that a persisted alert filter can constrain.
 */
export type PersistedFilterField =
  | 'channels'
  | 'sourceTypes'
  | 'deviceIds'
  | 'feedIds'
  | 'talkgroups'
  | 'talkgroupTags'
  | 'radioIds'
  | 'speakerIds'
  | 'tags';

/**
 * A single field constraint within a persisted filter.
 */
export interface PersistedFieldFilter {
  values: string[];
  mode: FilterMode;
}

/**
 * Selection over groupable entities: either "all" or an explicit list.
 */
export type GroupableSelection =
  | { all: true }
  | { selections: Array<{ kind: 'group' | 'value'; id: string }> };

/**
 * Persisted alert filter (saved-filter definition stored on an alert).
 */
export interface PersistedFilter {
  keyword?: string;
  emergency?: boolean;
  fields: Partial<Record<PersistedFilterField, PersistedFieldFilter>>;
  groupable: GroupableSelection;
}

/**
 * Alert entity from vb-server
 */
export interface Alert {
  alert_id: string;
  org_id: string;
  name: string;
  filter: PersistedFilter;
  channel_id?: string | null;
  keyword?: string | null;
  addresses: Record<string, AlertAddress>;
  notify_policy?: NotifyPolicy | null;
  status: number;
  /** Structural sub-type: 'export' = near-realtime export (delivery ledger, no fires). */
  kind: 'standard' | 'export';
  created: string;
  modified: string;
}

/**
 * Input for creating a new alert
 */
export interface CreateAlertInput {
  name: string;
  filter: PersistedFilter;
  keyword?: string | null;
  channel_id?: string | null;
  addresses?: Record<string, AlertAddressInput>;
  notify_policy?: NotifyPolicy | null;
  status?: number;
}

/**
 * Input for updating an alert
 */
export interface UpdateAlertInput {
  name?: string;
  filter?: PersistedFilter;
  keyword?: string;
  channel_id?: string | null;
  addresses?: Record<string, AlertAddressInput>;
  notify_policy?: NotifyPolicy | null;
  status?: number;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Strip the read-only, server-authoritative webhook `secret` from write payloads.
 * Edit mode seeds `addresses` from the alert read response (which still carries the
 * secret), so without this a plain rename would echo the secret back on save. The
 * server also strips it, but we keep the wire clean and the type honest.
 */
function sanitizeAddresses(
  addresses?: Record<string, AlertAddressInput>
): Record<string, AlertAddressInput> | undefined {
  if (!addresses) return addresses;
  const out: Record<string, AlertAddressInput> = {};
  for (const [id, addr] of Object.entries(addresses)) {
    // Drop any read-only, server-authoritative webhook secret fields that an
    // edit-mode alert may have seeded from the read response.
    const {
      secret: _secret,
      secret_set: _secretSet,
      secret_hint: _secretHint,
      ...writable
    } = addr as AlertAddressInput & {
      secret?: string;
      secret_set?: boolean;
      secret_hint?: string;
    };
    out[id] = writable;
  }
  return out;
}

/**
 * List all alerts for an organization
 *
 * @param orgId - Organization ID
 * @returns Array of alerts
 */
export async function listAlerts(orgId: string, signal?: AbortSignal): Promise<Alert[]> {
  return serverGet<Alert[]>(`/orgs/${orgId}/alerts`, signal);
}

/**
 * Get a single alert by ID
 *
 * @param orgId - Organization ID
 * @param alertId - Alert ID
 * @returns Alert or null if not found
 */
export async function getAlert(orgId: string, alertId: string): Promise<Alert | null> {
  try {
    const alerts = await listAlerts(orgId);
    return alerts.find((a) => a.alert_id === alertId) || null;
  } catch {
    return null;
  }
}

/**
 * Create a new alert
 *
 * @param orgId - Organization ID
 * @param data - Alert data
 * @returns Created alert
 */
export async function createAlert(orgId: string, data: CreateAlertInput): Promise<Alert> {
  return serverPost<Alert>(`/orgs/${orgId}/alerts`, {
    ...data,
    addresses: sanitizeAddresses(data.addresses)
  });
}

/**
 * Update an existing alert
 *
 * @param orgId - Organization ID
 * @param alertId - Alert ID
 * @param data - Updated alert data
 * @returns Updated alert
 */
export async function updateAlert(
  orgId: string,
  alertId: string,
  data: UpdateAlertInput
): Promise<Alert> {
  return serverPut<Alert>(`/orgs/${orgId}/alerts/${alertId}`, {
    ...data,
    addresses: sanitizeAddresses(data.addresses)
  });
}

/**
 * Delete an alert (soft delete)
 *
 * @param orgId - Organization ID
 * @param alertId - Alert ID
 */
export async function deleteAlert(orgId: string, alertId: string): Promise<void> {
  await serverDelete(`/orgs/${orgId}/alerts/${alertId}`);
}

/**
 * Enable/disable an alert
 *
 * @param orgId - Organization ID
 * @param alertId - Alert ID
 * @param enabled - Whether the alert should be enabled
 * @returns Updated alert
 */
export async function setAlertStatus(
  orgId: string,
  alertId: string,
  enabled: boolean
): Promise<Alert> {
  return updateAlert(orgId, alertId, { status: enabled ? 1 : 0 });
}

/** Alert limit configuration (export-limits design §3). */
export interface AlertLimits {
  exportForceThresholdPerDay: number;
  rateLimits?: { day: number; week: number };
}

/**
 * Fetch the org's alert limits. Resolves a disabled threshold on failure —
 * the editor treats that as warn-only UX rather than blocking on an outage.
 */
export async function getAlertLimits(orgId: string, signal?: AbortSignal): Promise<AlertLimits> {
  try {
    return await serverGet<AlertLimits>(`/orgs/${orgId}/alerts/limits`, signal);
  } catch {
    return { exportForceThresholdPerDay: 0 };
  }
}

/**
 * Reveal the full webhook signing secret for a single destination.
 * The value is masked in every other response; requires `alerts:manage`.
 *
 * @param orgId - Organization ID
 * @param alertId - Alert ID
 * @param addressId - Destination (address) ID
 * @returns The full signing secret
 */
export async function revealWebhookSecret(
  orgId: string,
  alertId: string,
  addressId: string
): Promise<string> {
  const { secret } = await serverGet<{ secret: string }>(
    `/orgs/${orgId}/alerts/${alertId}/addresses/${addressId}/secret`
  );
  return secret;
}

/**
 * Rotate the webhook signing secret for a single destination (hard cutover).
 * Returns the freshly minted value once; masked on every read thereafter. The
 * partner's verification breaks until they update their copy. Requires `alerts:manage`.
 *
 * @param orgId - Organization ID
 * @param alertId - Alert ID
 * @param addressId - Destination (address) ID
 * @returns The new signing secret
 */
export async function rotateWebhookSecret(
  orgId: string,
  alertId: string,
  addressId: string
): Promise<string> {
  const { secret } = await serverPost<{ secret: string }>(
    `/orgs/${orgId}/alerts/${alertId}/addresses/${addressId}/secret/rotate`,
    {}
  );
  return secret;
}
